import { useRef } from 'react'
import { Avatar } from 'primereact/avatar'
import { Menu } from 'primereact/menu'
import type { MenuItem } from 'primereact/menuitem'
import { useTranslation } from 'react-i18next'
import { useDisplayName } from '../auth/useCurrentUser'
import { useLogout } from '../auth/useLogout'

interface PersonaBadgeProps {
  variant?: 'sidebar' | 'topbar'
  /**
   * Kimlik menüsünün **başına** eklenecek öğeler (çıkıştan bir ayraçla ayrılır); `topbar` varyantına
   * özgüdür. Bileşen bu öğelerin nereden geldiğini bilmez: kimlikle ilgili özellikler (ör. vekalet
   * persona seçimi — 089) kendi mantığını kendi klasöründe tutar, buraya yalnız hazır menü öğesi verir.
   */
  extraMenuItems?: MenuItem[]
  /**
   * Rozette gösterilecek ad; verilmezse `/me`'nin görünen adı kullanılır (bugünkü davranış).
   *
   * <p>Bileşen bunu **hesaplamaz**, çünkü ek bilginin nereden geldiğini bilmemeli — `extraMenuItems`
   * ile aynı gerekçe. Bugünkü tek kullanıcısı asistan personası: menüde "Ahmet Olmuştur (Tam Yetki)"
   * seçen kişi, seçimden sonra rozette yalnız "Ahmet Olmuştur" görüyordu ve hangi yetkiyle
   * çalıştığını okuyamıyordu (kullanıcı bildirimi 20.10.2026, 126 FR-015).
   *
   * <p>Baş harfler yine **adın kendisinden** üretilir; etiketteki "(Tam Yetki)" ekinin avatara
   * sızması gerekmiyor.
   */
  label?: string
}

/** Görünen addan baş harfler (ilk iki sözcüğün ilk harfi). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0]?.[0] ?? ''
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + second).toUpperCase()
}

/**
 * Oturumdaki kullanıcı rozeti (FR-013): gerçek görünen ad + roller + baş harf avatarı ({@code /me}).
 * Topbar varyantında tıklanınca kimlik menüsü açılır: `extraMenuItems` (varsa) + çıkış (FR-017 —
 * RP-initiated logout).
 */
export default function PersonaBadge({
  variant = 'topbar',
  extraMenuItems,
  label,
}: Readonly<PersonaBadgeProps>) {
  const { t } = useTranslation()
  // const { data } = useCurrentUser()
  const displayName = useDisplayName() ?? ''
  const logout = useLogout()
  const menuRef = useRef<Menu>(null)

  const name = displayName || t('auth.account')
  // Baş harfler ADDAN üretilir, etiketten değil: etiket "(Tam Yetki)" eki taşıyabiliyor ve avatarda
  // "AT" gibi anlamsız bir kısaltma çıkardı.
  const shown = label || name
  // const role = data?.roles?.join(' · ') ?? ''
  const initials = initialsOf(name)

  if (variant !== 'topbar') {
    return (
      <div className={`persona persona-${variant}`}>
        <Avatar label={initials} shape="circle" />
        <div className="persona-info">
          <div className="persona-name">{shown}</div>
          {/* <div className="persona-role">{role}</div> */}
        </div>
      </div>
    )
  }

  // Ayraç yalnız gerçekten öğe varsa eklenir; yoksa menünün tepesinde sahipsiz bir çizgi kalırdı.
  const model: MenuItem[] = [
    ...(extraMenuItems ?? []),
    ...(extraMenuItems && extraMenuItems.length > 0 ? [{ separator: true }] : []),
    { label: t('auth.logout'), icon: 'pi pi-sign-out', command: () => logout() },
  ]

  return (
    <>
      <Menu popup ref={menuRef} model={model} />
      <button
        type="button"
        className="persona persona-topbar"
        /* Erişilebilir ad GÖRÜNÜR metni İÇERMELİ (WCAG 2.5.3 "Label in Name"). Eskiden burada
           sabit `auth.account` ("Hesap") vardı ve `aria-label` görünür içeriği EZDİĞİ için ekran
           okuyucu kullanıcısı rozetteki kipi ve yetki seviyesini hiç duymuyordu — oysa o bilgi
           126 FR-015 ile bilerek rozete kondu (kullanıcı isteği: "yanında yetki bilgisini de
           göreyim"). Sesli kontrol kullanıcısı da düğmeyi görünen adıyla çağıramıyordu.
           THY tarafındaki PR review'ü bunu bulgu olarak bildirdi (22.10.2026). */
        aria-label={t('auth.accountNamed', { name: shown })}
        onClick={(e) => menuRef.current?.toggle(e)}
      >
        <Avatar label={initials} shape="circle" />
        <div className="persona-info">
          <div className="persona-name">{shown}</div>
          {/* <div className="persona-role">{role}</div> */}
        </div>
        <svg
          className="persona-chevron"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          aria-hidden
        >
          <path d="M3 4.5l3 3 3-3" />
        </svg>
      </button>
    </>
  )
}
